import { MouseEvent, useState } from 'react';

import { Button, Divider, Stack } from '@mui/material';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import { PaymentMethodStatus } from '@/graphql';
import useAddPaymentMethod from '@/hooks/useAddPaymentMethod';
import useMonetaryCreditInfo, {
  useOrgSharedEndpointsCreditBalance,
} from '@/hooks/useMonetaryCreditInfo';
import { getBorder } from '@/theme/utils';
import { formatPrice } from '@/utils/currency';

import { HybridCreditCallout } from './CreditCallout/HybridCreditCallout';
import { HybridDotIcon, Button as ProgressButton } from './styled';

function Credits() {
  const { monetaryCreditInfo } = useMonetaryCreditInfo();
  const addPaymentMethod = useAddPaymentMethod();
  const { credits } = useOrgSharedEndpointsCreditBalance();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  if (
    !monetaryCreditInfo ||
    !credits ||
    (credits.remaining <= 0 &&
      monetaryCreditInfo.creditBalance <= 0 &&
      monetaryCreditInfo.paymentMethodStatus !== PaymentMethodStatus.NeedsPaymentMethod)
  ) {
    return null;
  }

  const { paymentMethodStatus } = monetaryCreditInfo;

  const creditsRemainingCents = credits.remaining * 100;
  const creditsIssuedCents = credits.issued * 100;

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  const dedicatedProgress =
    ((monetaryCreditInfo.creditGranted - monetaryCreditInfo.creditBalance) /
      monetaryCreditInfo.creditGranted) *
    100;

  const modelApisProgress =
    ((creditsIssuedCents - creditsRemainingCents) / creditsIssuedCents) * 100;

  const needsCreditCard = paymentMethodStatus === PaymentMethodStatus.NeedsPaymentMethod;
  const hasCreditCard = paymentMethodStatus === PaymentMethodStatus.HasPaymentMethod;

  const dedicatedDescription =
    needsCreditCard && monetaryCreditInfo.creditBalance <= 0
      ? 'Add a payment method to reactivate your models and deploy new versions.'
      : `${formatPrice(
          monetaryCreditInfo.creditBalance,
        )} of free credits remaining to deploy and serve models.`;

  const modelApisDescription =
    needsCreditCard && creditsRemainingCents <= 0
      ? 'Add a payment method to reactivate your Model APIs.'
      : `${formatPrice(creditsRemainingCents)} of free credits remaining to use Model APIs.`;

  return (
    <>
      <ProgressButton progress={0} onClick={handleClick} hasCreditCard={hasCreditCard}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography zIndex={1} variant="h5">
            <Stack direction="row">
              <HybridDotIcon hasCreditCard={hasCreditCard} progress={dedicatedProgress} />
              {formatPrice(monetaryCreditInfo.creditBalance)}
            </Stack>
          </Typography>
          <Typography
            zIndex={1}
            color="secondary"
            sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
          >
            for dedicated
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography zIndex={1} variant="h5">
            <Stack direction="row">
              <HybridDotIcon hasCreditCard={hasCreditCard} progress={modelApisProgress} />
              {formatPrice(creditsRemainingCents)}
            </Stack>
          </Typography>
          <Typography
            zIndex={1}
            color="secondary"
            sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
          >
            for APIs
          </Typography>
        </Stack>
      </ProgressButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={3}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Stack gap={2} p={2}>
          <HybridCreditCallout
            title="Dedicated deployments"
            description={dedicatedDescription}
            creditsRemainingCents={monetaryCreditInfo.creditBalance}
            creditsIssuedCents={monetaryCreditInfo.creditGranted}
            hasCreditCard={hasCreditCard}
          />
          <Divider />
          <HybridCreditCallout
            title="Model APIs"
            description={modelApisDescription}
            creditsRemainingCents={creditsRemainingCents}
            creditsIssuedCents={creditsIssuedCents}
            hasCreditCard={hasCreditCard}
          />
        </Stack>
        {needsCreditCard && (
          <Stack p={2} sx={{ borderTop: (theme) => getBorder(theme.palette.border.default) }}>
            <Button variant="contained" color="primary" onClick={addPaymentMethod}>
              Add payment method
            </Button>
          </Stack>
        )}
      </Popover>
    </>
  );
}

export default Credits;
